import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Download, Trash2, Ban, CreditCard, KeyRound, Server } from 'lucide-react';
import { useSeo } from '../lib/useSeo';

const PILLARS = [
  {
    icon: Lock,
    title: 'Encrypted, end to end in transit',
    body: 'Everything travels over encrypted TLS connections. High-risk secrets — like your calendar authorization tokens and messaging keys — are encrypted at rest with a dedicated application key and only decrypted in memory when needed.',
  },
  {
    icon: KeyRound,
    title: 'Passwords you control',
    body: 'Your password is stored only as a one-way hash — we never keep it in plain text and can never see it. Repeated failed logins are rate-limited to protect your account.',
  },
  {
    icon: Download,
    title: 'Export anytime',
    body: 'Download everything you have in Larkwell as a single JSON file from Settings → Your data & privacy. Your data is yours — take it with you whenever you like.',
  },
  {
    icon: Trash2,
    title: 'Delete for good',
    body: 'Permanently remove your account and all associated data in one tap. When you disconnect a calendar, we revoke access and remove the stored authorization.',
  },
  {
    icon: Ban,
    title: 'Never sold, never rented',
    body: 'We do not sell, rent, or share your personal data for advertising. Context sent to our AI provider is used only to produce your result — never to train models on your private data.',
  },
  {
    icon: CreditCard,
    title: 'Payments handled by Stripe',
    body: 'All card details are processed directly by Stripe, a PCI-DSS Level 1 certified provider. Larkwell never sees or stores your card number.',
  },
];

export default function Security() {
  useSeo({
    title: 'Trust & Security · Larkwell',
    description: 'How Larkwell keeps your data safe: encryption at rest and in transit, one-tap export and deletion, and GDPR & CCPA aligned privacy practices.',
    canonical: 'https://larkwellapp.com/security',
  });
  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      <div className="max-w-4xl mx-auto px-6 py-14">
        <Link to="/" className="text-sm font-500" style={{ color: 'hsl(var(--primary))' }} data-testid="security-home-link">← Back to Larkwell</Link>

        <div className="flex items-center gap-2 mt-6 mb-2">
          <ShieldCheck className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} />
          <h1 className="font-display text-3xl sm:text-4xl font-700 tracking-tight">Trust &amp; Security</h1>
        </div>
        <p className="text-[hsl(var(--muted-foreground))] max-w-2xl text-[15px] leading-relaxed">
          Larkwell holds some of your most personal thoughts — your tasks, your plans, the things on your mind. We treat that with the care it deserves. Here's exactly how we protect it.
        </p>

        {/* Pillars */}
        <div className="grid sm:grid-cols-2 gap-4 mt-10" data-testid="security-pillars">
          {PILLARS.map(({ icon: Icon, title, body }, i) => (
            <div
              key={title}
              className="rounded-2xl border border-[hsl(var(--border))] bg-card p-6"
              data-testid={`security-pillar-${i}`}
            >
              <div className="h-10 w-10 rounded-xl grid place-items-center mb-4" style={{ background: 'hsl(var(--primary) / 0.12)' }}>
                <Icon className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
              </div>
              <h2 className="font-display text-lg font-600 mb-1.5">{title}</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* Compliance */}
        <div className="mt-12 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-6 sm:p-8" data-testid="security-compliance">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
            <h2 className="font-display text-xl font-600">GDPR &amp; CCPA aligned</h2>
          </div>
          <p className="text-[15px] text-[hsl(var(--muted-foreground))] leading-relaxed">
            Larkwell is built around the privacy rights at the heart of the EU's GDPR and California's CCPA: the right to <strong className="text-[hsl(var(--foreground))]">access</strong> and export your data, the right to <strong className="text-[hsl(var(--foreground))]">correct</strong> it, and the right to <strong className="text-[hsl(var(--foreground))]">delete</strong> it entirely. We collect only what we need to run the product, and we never sell your personal data.
          </p>
          <p className="text-[15px] text-[hsl(var(--muted-foreground))] leading-relaxed mt-3">
            Want to exercise a data right or ask a privacy question? Email{' '}
            <a href="mailto:support@larkwellapp.com" className="font-600 underline underline-offset-2" style={{ color: 'hsl(var(--primary))' }} data-testid="security-contact-email">support@larkwellapp.com</a>{' '}
            and we'll respond within 30 days. For general inquiries, press, or partnerships, email{' '}
            <a href="mailto:info@larkwellapp.com" className="font-600 underline underline-offset-2" style={{ color: 'hsl(var(--primary))' }} data-testid="security-info-email">info@larkwellapp.com</a>.
          </p>
        </div>

        {/* Infrastructure note */}
        <div className="mt-6 flex items-start gap-3 text-sm text-[hsl(var(--muted-foreground))]" data-testid="security-infra-note">
          <Server className="h-5 w-5 shrink-0 mt-0.5" style={{ color: 'hsl(var(--primary))' }} />
          <p className="leading-relaxed">
            Larkwell runs on hardened, managed cloud infrastructure with encrypted data stores. We're an independent team and we're transparent about where we are on our security journey — if your organization needs specific documentation, just reach out.
          </p>
        </div>

        <footer className="mt-12 pt-6 border-t border-[hsl(var(--border))] flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="security-footer-home">Home</Link>
          <Link to="/privacy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="security-footer-privacy">Privacy &amp; data</Link>
          <Link to="/terms" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="security-footer-terms">Terms of Service</Link>
          <Link to="/login" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="security-footer-login">Sign in</Link>
        </footer>
      </div>
    </div>
  );
}
